import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import TransactionsTable from "@/components/TransactionsTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FilterX } from "lucide-react";

const TYPES = ["Earn", "Redeem", "Reversal", "Referral Bonus"];
const SERVICES = ["Flights", "Hotels", "Packages", "Visa", "Other"];
const emptyFilters = { tx_type: "", service_type: "", processed_by: "", from_date: "", to_date: "" };

function buildParams(f) {
  const p = new URLSearchParams();
  ["tx_type", "service_type", "processed_by"].forEach((k) => f[k] && p.set(k, f[k]));
  if (f.from_date) p.set("from_date", new Date(`${f.from_date}T00:00:00`).toISOString());
  if (f.to_date) p.set("to_date", new Date(`${f.to_date}T23:59:59.999`).toISOString());
  return p;
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1">
      <Label className="label-caps">{label}</Label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger className="w-40" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ReverseDialog({ tx, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [available, setAvailable] = useState(null);
  useEffect(() => {
    setAvailable(null);
    if (!tx) return;
    api.get(`/customers/${tx.customer_id}`).then(({ data }) =>
      setAvailable((data.reward_balance || 0) - (data.held_points || 0))).catch(() => {});
  }, [tx]);
  const short = tx && tx.points > 0 && available !== null && available < tx.points;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/rewards/reverse", { transaction_id: tx.transaction_id, reason });
      toast.success(`Reversed ${tx.transaction_id}`);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reverse-dialog">
        <DialogHeader><DialogTitle>Reverse {tx?.transaction_id}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>
            {tx?.customer_name} · {tx?.type} · {tx?.points > 0 ? "+" : ""}{tx?.points} pts. A Reversal
            entry of {-(tx?.points || 0)} pts will be added to the ledger.
          </div>
          {short && (
            <div className="rounded-lg p-3 text-sm bg-amber-50 text-amber-800 border border-amber-200"
                 data-testid="reverse-balance-warning">
              Warning: the member's available balance ({available} pts) is lower than the reversal amount
              ({tx.points} pts). The balance cannot go negative, so this reversal will be refused until the
              member has enough points.
            </div>
          )}
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason (e.g. booking refunded)" data-testid="reverse-reason-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reverse-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3}
                  className="bg-red-600 hover:bg-red-700 text-white" data-testid="reverse-confirm-btn">
            {busy ? "Reversing…" : "Confirm reversal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LedgerPanel({ staff = [], canReverse = false, onChanged }) {
  const [filters, setFilters] = useState(emptyFilters);
  const [txs, setTxs] = useState([]);
  const [reverseTx, setReverseTx] = useState(null);
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/transactions?${buildParams(filters)}`);
      setTxs(data);
    } catch (e) {
      toast.error(formatError(e));
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const staffNames = useMemo(() => {
    const names = new Set(staff.map((s) => s.username));
    txs.forEach((t) => t.processed_by && names.add(t.processed_by));
    return [...names].sort();
  }, [staff, txs]);

  const exportCsv = async () => {
    const token = localStorage.getItem("zola_token");
    const res = await fetch(`${api.defaults.baseURL}/reports/transactions.csv?${buildParams(filters)}`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Export failed"); return; }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(await res.blob());
    a.download = "zola-transactions.csv";
    a.click();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-xl font-bold">Full Ledger</h2>
        <Button variant="outline" onClick={exportCsv} data-testid="ledger-csv-export" className="rounded-xl">
          Export CSV
        </Button>
      </div>
      <div className="w-card-flat p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="ledger-filters">
        <FilterSelect label="Type" value={filters.tx_type} onChange={set("tx_type")} options={TYPES} testid="ledger-type-filter" />
        <FilterSelect label="Service" value={filters.service_type} onChange={set("service_type")} options={SERVICES} testid="ledger-service-filter" />
        <FilterSelect label="Staff" value={filters.processed_by} onChange={set("processed_by")} options={staffNames} testid="ledger-staff-filter" />
        <div className="space-y-1">
          <Label className="label-caps">From</Label>
          <Input type="date" className="w-40" value={filters.from_date}
                 onChange={(e) => set("from_date")(e.target.value)} data-testid="ledger-from-date" />
        </div>
        <div className="space-y-1">
          <Label className="label-caps">To</Label>
          <Input type="date" className="w-40" value={filters.to_date}
                 onChange={(e) => set("to_date")(e.target.value)} data-testid="ledger-to-date" />
        </div>
        <Button variant="ghost" onClick={() => setFilters(emptyFilters)} data-testid="ledger-clear-filters">
          <FilterX className="h-4 w-4 mr-1.5" /> Clear
        </Button>
        <div className="ml-auto text-xs" style={{ color: "var(--text-muted)" }} data-testid="ledger-count">
          {txs.length} entries
        </div>
      </div>
      <TransactionsTable transactions={txs} showCustomer showProfit onReverse={canReverse ? setReverseTx : null} />
      <ReverseDialog tx={reverseTx} onClose={() => setReverseTx(null)}
                     onDone={() => { setReverseTx(null); load(); onChanged?.(); }} />
    </div>
  );
}
